import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Clock,
  Mail,
  User,
  Calendar,
  AlertCircle as AlertCircleIcon,
  CheckCircle,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { Spinner } from '../../design-system';

interface LeadQuestionnaire {
  id: string;
  user_id: string;
  email: string;
  status: 'pending' | 'completed';
  dismissal_count: number;
  role: string | null;
  team_size: string | null;
  what_brought_you: string | null;
  adoption_process: string | null;
  how_heard: string | null;
  biggest_challenge: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface ListResponse {
  items: LeadQuestionnaire[];
  total: number;
  skip: number;
  limit: number;
}

const statusConfig = {
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-brand-primary',
    bg: 'bg-brand-primary/10',
    border: 'border-brand-primary/20',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    border: 'border-orange-200 dark:border-orange-800',
  },
};

const LeadQuestionnaireSection: React.FC = () => {
  const api = useAuthenticatedApi();

  const [items, setItems] = useState<LeadQuestionnaire[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [skip, setSkip] = useState(0);
  const limit = 20;

  const [selectedItem, setSelectedItem] = useState<LeadQuestionnaire | null>(null);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });
      const response: ListResponse = await api.get(
        `/admin/lead-questionnaires?${params.toString()}`
      );
      setItems(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch lead questionnaires');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const hasNext = skip + limit < total;
  const hasPrev = skip > 0;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatusBadge = ({ status }: { status: 'pending' | 'completed' }) => {
    const config = statusConfig[status];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color} border ${config.border}`}>
        <Icon className="w-3 h-3 flex-shrink-0" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-w-0 overflow-x-hidden">
      <div className="p-4 sm:p-6">
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-semibold text-ui-text-primary mb-2">
              Lead Questionnaires
            </h2>
            <p className="text-sm text-ui-text-tertiary">
              {total} response{total !== 1 ? 's' : ''} total
            </p>
          </div>
          <button
            onClick={fetchItems}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-ui-border-primary text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Loading State */}
        {loading && items.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={24} />
          </div>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <div className="text-center py-12">
            <p className="text-ui-text-tertiary">No questionnaire responses yet.</p>
          </div>
        )}

        {/* Desktop Table */}
        {items.length > 0 && (
          <>
            <div className="hidden xl:block overflow-x-auto rounded-lg border border-ui-border-primary">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ui-background-tertiary dark:bg-neutral-800/50 border-b border-ui-border-primary">
                    <th className="text-left px-4 py-3 font-medium text-ui-text-secondary">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-ui-text-secondary">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-ui-text-secondary">Team Size</th>
                    <th className="text-left px-4 py-3 font-medium text-ui-text-secondary">How Heard</th>
                    <th className="text-left px-4 py-3 font-medium text-ui-text-secondary">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-ui-text-secondary">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedItem(item)}
                      className="border-b border-ui-border-primary hover:bg-ui-background-tertiary cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-ui-text-primary font-medium truncate max-w-[200px]">
                        {item.email}
                      </td>
                      <td className="px-4 py-3 text-ui-text-secondary">
                        {item.role || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-ui-text-secondary">
                        {item.team_size || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-ui-text-secondary truncate max-w-[180px]">
                        {item.how_heard || '\u2014'}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-3 text-ui-text-tertiary whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="xl:hidden space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="p-4 rounded-lg border border-ui-border-primary hover:bg-ui-background-tertiary cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-ui-text-primary truncate">
                      {item.email}
                    </span>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="space-y-1 text-xs text-ui-text-tertiary">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      {item.role || 'No role'}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-ui-border-primary">
                <p className="text-sm text-ui-text-tertiary">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSkip(skip - limit)}
                    disabled={!hasPrev}
                    className="p-2 rounded-lg border border-ui-border-primary text-ui-text-secondary hover:bg-ui-background-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSkip(skip + limit)}
                    disabled={!hasNext}
                    className="p-2 rounded-lg border border-ui-border-primary text-ui-text-secondary hover:bg-ui-background-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setSelectedItem(null)}
          />
          <div
            className="relative bg-ui-background-secondary rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto border border-ui-border-primary animate-in zoom-in-95 slide-in-from-bottom-2 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-ui-border-primary">
              <div>
                <h3 className="text-lg font-semibold text-ui-text-primary">
                  Questionnaire Response
                </h3>
                <p className="text-sm text-ui-text-tertiary mt-0.5">
                  {selectedItem.email}
                </p>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors p-1 rounded-lg hover:bg-ui-background-tertiary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 space-y-4">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-ui-text-tertiary mb-1">Status</p>
                  <StatusBadge status={selectedItem.status} />
                </div>
                <div>
                  <p className="text-xs font-medium text-ui-text-tertiary mb-1">Dismissals</p>
                  <p className="text-sm text-ui-text-primary">{selectedItem.dismissal_count}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-ui-text-tertiary mb-1">Created</p>
                  <p className="text-sm text-ui-text-primary">{formatDate(selectedItem.created_at)}</p>
                </div>
                {selectedItem.completed_at && (
                  <div>
                    <p className="text-xs font-medium text-ui-text-tertiary mb-1">Completed</p>
                    <p className="text-sm text-ui-text-primary">{formatDate(selectedItem.completed_at)}</p>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-ui-border-primary" />

              {/* Answers */}
              <div className="space-y-3">
                <DetailField label="User ID" value={selectedItem.user_id} />
                <DetailField label="Role" value={selectedItem.role} />
                <DetailField label="Team Size" value={selectedItem.team_size} />
                <DetailField label="What Brought You" value={selectedItem.what_brought_you} />
                <DetailField label="Adoption Process" value={selectedItem.adoption_process} />
                <DetailField label="How Heard" value={selectedItem.how_heard} />
                <DetailField label="Biggest Challenge" value={selectedItem.biggest_challenge} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailField = ({ label, value }: { label: string; value: string | null }) => (
  <div>
    <p className="text-xs font-medium text-ui-text-tertiary mb-0.5">{label}</p>
    <p className="text-sm text-ui-text-primary">
      {value || <span className="text-ui-text-tertiary">&mdash;</span>}
    </p>
  </div>
);

export default LeadQuestionnaireSection;
